import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Participant } from '../../modals/participant';
import { Transaction } from '../../modals/transactions';
import { AuthService } from '../auth.service';
import { Camp, StepUrlMapping } from '../../modals/camp';

@Injectable({
  providedIn: 'root',
})
export class WebServiceService {
  appRoot = '';
  apiVersion = 'apiV2/';
  authToken = '';

  constructor(private http: HttpClient, private authService: AuthService) {
    this.appRoot = environment.functionsURL;
    this.authService.afAuth.authState.subscribe(async user => {
      if (!user) return;
      this.authToken = await user.getIdToken();
    })
  }

  async refreshToken(): Promise<void> {
    const user = await this.authService.afAuth.currentUser;
    if (user) {
      this.authToken = await user.getIdToken(true);
    }
  }

  private getAuthHeader(authHeader?: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Bearer ' + (authHeader ? authHeader : this.authToken),
    });
  }

  public postRegisterNewUserToCamp = (
    participant: Participant,
    userId: string,
    campId: string,
    authToken?: string,
  ) => {
    return this.http.post(this.appRoot + this.apiVersion + 'registerNewUserToCamp', { participant, userId, campId }, {
      headers: this.getAuthHeader(authToken)
    });
  };

  public getCampregistrationsByUser = (userId: string) => {
    return this.http.get(
      this.appRoot + this.apiVersion + 'getCampregistrationsByUser?userId=' + userId, {
      headers: this.getAuthHeader()
    }
    );
  };

  public verifyParent = (userId: string, participantId: string, parentId: string, password: string) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'verifyParent?userId=' + userId, {
      userId,
      participantId,
      parentId,
      password
    }, {
      headers: this.getAuthHeader()
    }
    );
  }
  public getUserGroups = () => {
    return this.http.get(this.appRoot + this.apiVersion + 'getUserGroups', {
      headers: this.getAuthHeader()
    });
  };

  public updateProperties = (campRegistrationId: string, updateProperty: string, updateObject: string, value: string) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'updateProperties', {
      campRegistrationId,
      updateProperty,
      updateObject,
      value,
    }, {
      headers: this.getAuthHeader()
    }
    );
  }

  public sendRegistrationConfirmation = (campRegistrationId: string) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/sendRegistrationConfirmation', {
      campRegistrationId,
    }, {
      headers: this.getAuthHeader()
    }
    );
  }

  public sendParentReminderEmail = (campRegistrationId: string) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/sendParentReminderEmail', {
      campRegistrationId,
    }, {
      headers: this.getAuthHeader()
    }
    );
  }

  public sendMoneyReminderEmail = (campRegistrationId: string) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/sendMoneyReminderEmail', {
      campRegistrationId,
    }, {
      headers: this.getAuthHeader()
    }
    );
  }

  public sendAddTransaction = (campRegistrationId: string, transaction: Transaction, sendEmail: boolean) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/addTransaction', {
      campRegistrationId,
      transaction: transaction,
      sendEmail
    }, {
      headers: this.getAuthHeader()
    }
    );
  }

  public sendSwimming = (participantId: string, value: boolean) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/swimming', {
      participantId,
      value
    }, {
      headers: this.getAuthHeader()
    }
    );
  }

  public sendUnregisterEvent = (campRegistrationId: string, message: string) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/unregisterFromEvent', {
      campRegistrationId,
      message
    }, {
      headers: this.getAuthHeader()
    }
    );
  }

  public createUpdateCamp = (mode: string, camp: Camp) => {
    const mergedCamp = {
      ...camp,
      registrationCompleteSteps: camp.registrationCompleteSteps.map((step: any) => {
        return { url: step.url, stepId: step.stepId?.path || '/registration-complete-steps/' + step.id }
      })
    }
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/createUpdateCamp', {
      mode,
      camp: mergedCamp
    }, {
      headers: this.getAuthHeader()
    }
    )
  }

  public postRegisterExistingUserToCamp = (
    participantId: string,
    userId: string,
    campId: string,
    authToken?: string,
  ) => {
    return this.http.post(this.appRoot + this.apiVersion + 'registerExistingUserToCamp', { participantId, userId, campId }, {
      headers: this.getAuthHeader(authToken)
    });
  };

  public getParticipantsByUser = (userId: string) => {
    return this.http.get(
      this.appRoot + this.apiVersion + 'getParticipantsByUser?userId=' + userId, {
      headers: this.getAuthHeader()
    }
    );
  };

  public updateSignature = (
    signatureId: string,
    data: string,
    campRegistrationId?: string,
    signatureIdentifyer?: string,
    authToken?: string,
  ) => {
    return this.http.post(this.appRoot + this.apiVersion + 'updateSignature', { signatureId, data, campRegistrationId, signatureIdentifyer }, {
      headers: this.getAuthHeader(authToken)
    });
  };

  public approveSignature = (
    campRegistrationId: string,
    signatureIdentifyer: string,
    authToken?: string) => {
    return this.http.post(this.appRoot + this.apiVersion + 'admin/approveSignature', { campRegistrationId, signatureIdentifyer }, {
      headers: this.getAuthHeader(authToken)
    });
  }

  public deleteCamp = (
    campId: string,
    authToken?: string
  ) => {
    return this.http.delete(this.appRoot + this.apiVersion + 'admin/camp/' + campId, {
      headers: this.getAuthHeader(authToken)
    });
  }

  public updateParticipant = (
    participantId: string,
    data: any,
    authToken?: string,
  ) => {
    return this.http.post(this.appRoot + this.apiVersion + 'updateParticipant/' + participantId, { data }, {
      headers: this.getAuthHeader(authToken)
    });
  };

  public sendBeforeEmail = (campId: string) => {
    return this.http.post(
      this.appRoot + this.apiVersion + 'admin/sendBeforeEmail', {
      campId,
    }, {
      headers: this.getAuthHeader()
    }
    );
  }
}
